/**
 * Runtime validation for action-log entries and patches arriving over the
 * API. Structural only — whether an entry APPLIES is the replay's job (a
 * bad inline run there is an `invalid_patch` conflict, not an exception).
 */
import { z } from 'zod';
import { inlineNodeSchema } from '../ir/schema.js';
import { ACTION_LOG_SCHEMA, ACTION_LOG_VERSION } from './types.js';
const nodePath = z.array(z.union([z.string().min(1).max(32), z.number().int().min(0).max(100_000)])).max(64);
const fingerprint = z.string().min(1).max(64);
const seq = z.number().int().min(1).max(1_000_000);
const baseAnchor = z
    .object({
    instanceId: z.string().min(1).max(200),
    sectionId: z.string().min(1).max(200).optional(),
    path: nodePath.optional(),
})
    .strict();
const logAnchor = z.union([baseAnchor, z.object({ fromSeq: seq, path: nodePath.optional() }).strict()]);
const patchAnchor = z.union([baseAnchor, z.object({ fromPatch: z.string().min(1).max(200), path: nodePath.optional() }).strict()]);
const position = z.enum(['before', 'after', 'append']);
const inline = z.array(inlineNodeSchema).max(2000);
const replacePayload = z.object({ content: inline }).strict();
const removePayload = z.object({}).strict().optional();
const paragraphPayload = z
    .object({ position, content: inline, variant: z.enum(['body', 'lead', 'fine_print']).optional() })
    .strict();
const clausePayload = z.object({ position, title: z.string().min(1).max(500), paragraphs: z.array(inline).max(100) }).strict();
const rowPayload = z
    .object({ term: inline.optional(), description: z.array(inline).max(100).optional() })
    .strict()
    .refine((p) => p.term !== undefined || p.description !== undefined, { message: 'term or description required' });
const insertRowPayload = z.object({ position, term: inline, description: z.array(inline).max(100) }).strict();
const meta = { author: z.string().min(1).max(200), at: z.string().min(1).max(64) };
const proposal = {
    proposal: z.literal(true).optional(),
    blockVersion: z.string().regex(/^[A-Za-z0-9_-]{1,32}\.\d{1,6}$/).optional(),
    structureFingerprint: fingerprint.optional(),
};
const fieldKey = z.string().regex(/^custom_[a-z0-9_]{1,60}$/);
const fieldType = z.enum(['text', 'date', 'number', 'money', 'boolean']);
const fieldLabel = z.string().trim().min(1).max(200);
const newEntryVariants = [
    z.object({ ...meta, ...proposal, op: z.literal('replaceText'), anchor: logAnchor, fingerprint, payload: replacePayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('removeNode'), anchor: logAnchor, fingerprint, payload: removePayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('insertParagraph'), anchor: logAnchor, fingerprint: fingerprint.optional(), payload: paragraphPayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('insertClause'), anchor: logAnchor, fingerprint: fingerprint.optional(), payload: clausePayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('replaceRow'), anchor: logAnchor, fingerprint, payload: rowPayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('removeRow'), anchor: logAnchor, fingerprint, payload: removePayload }).strict(),
    z.object({ ...meta, ...proposal, op: z.literal('insertRow'), anchor: logAnchor, fingerprint: fingerprint.optional(), payload: insertRowPayload }).strict(),
    z
        .object({
        ...meta,
        ...proposal,
        op: z.literal('moveSection'),
        anchor: logAnchor,
        fingerprint,
        payload: z.object({ to: logAnchor, toFingerprint: fingerprint.optional(), position }).strict(),
    })
        .strict(),
    z
        .object({
        ...meta,
        op: z.literal('defineField'),
        payload: z.object({ key: fieldKey, label: fieldLabel, type: fieldType, help: z.string().max(1000).optional() }).strict(),
    })
        .strict(),
    z
        .object({
        ...meta,
        op: z.literal('updateField'),
        payload: z
            .object({ key: fieldKey, label: fieldLabel.optional(), type: fieldType.optional(), help: z.string().max(1000).nullable().optional() })
            .strict(),
    })
        .strict(),
    z.object({ ...meta, op: z.literal('removeField'), payload: z.object({ key: fieldKey }).strict() }).strict(),
    z.object({ ...meta, op: z.literal('acceptSuggestion'), payload: z.object({ targetSeq: seq }).strict() }).strict(),
    z.object({ ...meta, op: z.literal('rejectSuggestion'), payload: z.object({ targetSeq: seq }).strict() }).strict(),
    z
        .object({
        ...meta,
        op: z.literal('undo'),
        payload: z.object({ targetSeq: seq, reason: z.enum(['undo', 'restore', 'drop']).optional() }).strict(),
    })
        .strict(),
    z
        .object({
        ...meta,
        op: z.literal('rebase'),
        anchor: logAnchor.optional(),
        fingerprint: fingerprint.optional(),
        payload: z
            .object({
            targetSeq: seq,
            resolution: z.enum(['keep_as_text', 'reanchor']),
            to: logAnchor.optional(),
            toFingerprint: fingerprint.optional(),
        })
            .strict(),
    })
        .strict(),
];
/** An entry as the client submits it — the server assigns `seq`, `author`, `at`. */
export const newActionLogEntrySchema = z.discriminatedUnion('op', newEntryVariants);
export const actionLogEntrySchema = z.discriminatedUnion('op', newEntryVariants.map((v) => v.extend({ seq })));
export const actionLogSchema = z
    .object({
    schema: z.literal(ACTION_LOG_SCHEMA),
    version: z.literal(ACTION_LOG_VERSION),
    entries: z.array(actionLogEntrySchema).max(5000),
})
    .strict()
    .superRefine((log, ctx) => {
    for (let i = 1; i < log.entries.length; i++) {
        if (log.entries[i].seq <= log.entries[i - 1].seq) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['entries', i, 'seq'], message: 'seq must be strictly increasing' });
        }
    }
});
const patchMeta = {
    id: z.string().min(1).max(200),
    author: z.string().max(200).optional(),
    at: z.string().max(64).optional(),
    blockVersion: proposal.blockVersion,
    structureFingerprint: proposal.structureFingerprint,
};
export const documentPatchSchema = z.discriminatedUnion('op', [
    z.object({ ...patchMeta, op: z.literal('replaceText'), anchor: patchAnchor, fingerprint, payload: replacePayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('removeNode'), anchor: patchAnchor, fingerprint, payload: removePayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('insertParagraph'), anchor: patchAnchor, fingerprint: fingerprint.optional(), payload: paragraphPayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('insertClause'), anchor: patchAnchor, fingerprint: fingerprint.optional(), payload: clausePayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('replaceRow'), anchor: patchAnchor, fingerprint, payload: rowPayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('removeRow'), anchor: patchAnchor, fingerprint, payload: removePayload }).strict(),
    z.object({ ...patchMeta, op: z.literal('insertRow'), anchor: patchAnchor, fingerprint: fingerprint.optional(), payload: insertRowPayload }).strict(),
    z
        .object({
        ...patchMeta,
        op: z.literal('moveSection'),
        anchor: patchAnchor,
        fingerprint,
        payload: z.object({ to: patchAnchor, toFingerprint: fingerprint.optional(), position }).strict(),
    })
        .strict(),
]);
const parseWith = (schema) => (input) => {
    const parsed = schema.safeParse(input);
    if (parsed.success)
        return { ok: true, value: parsed.data };
    return { ok: false, issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })) };
};
export const parseActionLog = parseWith(actionLogSchema);
export const parseNewActionLogEntry = parseWith(newActionLogEntrySchema);
export const parseDocumentPatch = parseWith(documentPatchSchema);
//# sourceMappingURL=schema.js.map